import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, MapPin, GraduationCap, Shield, ShoppingBag, ArrowRight, Home, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { NEIGHBORHOOD_DATA, FEATURED_LISTINGS } from '../data/mockData';
import { ddfAPI } from '../services/api';
import LeadCaptureForm from '../components/forms/LeadCaptureForm';

const API = process.env.REACT_APP_BACKEND_URL;

// Stable fallback images per city (used until live MLS photo loads, or if DDF
// has no active inventory for that city). Sourced from Unsplash so they don't
// expire like MLS listing URLs do.
const CITY_FALLBACKS = {
  'brantford':   'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1600&q=80&auto=format&fit=crop',
  'hamilton':    'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=1600&q=80&auto=format&fit=crop',
  'burlington':  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80&auto=format&fit=crop',
  'oakville':    'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600&q=80&auto=format&fit=crop',
  'mississauga': 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1600&q=80&auto=format&fit=crop',
  'cambridge':   'https://images.unsplash.com/photo-1572120360610-d971b9d7767c?w=1600&q=80&auto=format&fit=crop',
  'kitchener':   'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1600&q=80&auto=format&fit=crop',
  'guelph':      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=80&auto=format&fit=crop',
  'waterloo':    'https://images.unsplash.com/photo-1600585154363-67eb9e2e2099?w=1600&q=80&auto=format&fit=crop',
  'milton':      'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=1600&q=80&auto=format&fit=crop',
  'ancaster':    'https://images.unsplash.com/photo-1600573472556-e636c2acda88?w=1600&q=80&auto=format&fit=crop',
  'paris':       'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1600&q=80&auto=format&fit=crop',
  'caledonia':   'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1600&q=80&auto=format&fit=crop',
  'grimsby':     'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80&auto=format&fit=crop',
  'burford':     'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1600&q=80&auto=format&fit=crop',
  'hagersville': 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1600&q=80&auto=format&fit=crop',
  'dundas':      'https://images.unsplash.com/photo-1600573472556-e636c2acda88?w=1600&q=80&auto=format&fit=crop',
  'st-george':   'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1600&q=80&auto=format&fit=crop',
};

const _cityImageCache = {};

const NeighbourhoodDetailPage = () => {
  const { id } = useParams();
  const [liveData, setLiveData] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityImage, setCityImage] = useState(_cityImageCache[id] || CITY_FALLBACKS[id] || CITY_FALLBACKS['brantford']);

  // Get mock data as fallback
  const mockData = NEIGHBORHOOD_DATA[id];

  // Live MLS hero image: ask backend for one current listing photo from this
  // city. If found, swap in. Otherwise the Unsplash fallback stays.
  useEffect(() => {
    if (_cityImageCache[id]) {
      setCityImage(_cityImageCache[id]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/ddf/neighborhood-image/${encodeURIComponent(id)}`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled || !data.image) return;
        // Only take the DDF image if it's a real listing photo. Backend's own
        // generic fallback is a single image for every city → would clobber
        // the good per-city fallback we start with.
        if (data.source !== 'ddf') return;
        _cityImageCache[id] = data.image;
        setCityImage(data.image);
      } catch (_) { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Convert URL id to city name (e.g., "brantford" -> "Brantford")
        const cityName = id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        
        // Fetch city-specific market stats (much faster than fetching all cities)
        const cityData = await ddfAPI.getCityMarketStats(cityName);
        
        if (cityData && (cityData.avgSoldPrice > 0 || cityData.soldCount > 0)) {
          setLiveData({
            name: cityData.city,
            avgPrice: cityData.avgSoldPrice || cityData.avgPrice || 0,
            priceChange: 5.2, // Would need historical data
            activeListings: cityData.soldCount || 0,
            avgDaysOnMarket: cityData.avgDaysOnMarket || 0,
            medianPrice: cityData.medianPrice || 0,
            priceRange: cityData.priceRange || { min: 0, max: 0 },
            dataType: cityData.dataType || 'active',
          });
        }

        // Fetch properties for this area
        const propsData = await ddfAPI.getProperties({ city: cityName, limit: 6 });
        if (propsData?.properties) {
          setListings(propsData.properties.slice(0, 3));
        }
      } catch (error) {
        console.error('Error fetching neighbourhood data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  // Use live data if available, otherwise fall back to mock data
  const data = liveData || mockData;
  const displayListings = listings.length > 0 ? listings : FEATURED_LISTINGS.slice(0, 3);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-red-600 mx-auto" />
          <p className="text-gray-500 mt-2">Loading neighbourhood data...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Neighbourhood Not Found</h1>
          <Link to="/neighbourhoods">
            <Button className="bg-red-600 hover:bg-red-700">View All Neighbourhoods</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Format display name
  const displayName = data.name || id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const priceChange = data.priceChange || 0;
  const activeListings = data.activeListings || 0;
  const avgDaysOnMarket = data.avgDaysOnMarket || 0;
  const avgPrice = data.avgPrice || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="relative h-[40vh] min-h-[300px]">
        <img
          src={cityImage}
          alt={displayName}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 text-white/80 mb-2">
              <Link to="/neighbourhoods" className="hover:text-white">Neighbourhoods</Link>
              <span>/</span>
              <span>{displayName}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{displayName}</h1>
            <div className="flex flex-wrap gap-3">
              {avgPrice > 0 && (
                <Badge className="bg-red-600 text-lg px-4 py-1">
                  Avg. ${avgPrice.toLocaleString()}
                </Badge>
              )}
              {priceChange !== 0 && (
                <Badge className={`text-lg px-4 py-1 ${priceChange >= 0 ? 'bg-green-600' : 'bg-red-600'}`}>
                  {priceChange >= 0 ? <TrendingUp size={16} className="mr-1" /> : <TrendingDown size={16} className="mr-1" />}
                  {priceChange >= 0 ? '+' : ''}{priceChange}% YoY
                </Badge>
              )}
              <Badge variant="outline" className="text-white border-white/50 text-lg px-4 py-1">
                {activeListings} {liveData?.dataType === 'sold' ? 'Recently Sold' : 'Active Listings'}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* About */}
            <Card>
              <CardHeader>
                <CardTitle>About {displayName}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  {mockData?.description || `Discover the vibrant community of ${displayName}. With ${activeListings} properties on the market and an average price of $${avgPrice.toLocaleString()}, this area offers excellent opportunities for homebuyers. Properties in ${displayName} typically spend ${avgDaysOnMarket} days on the market.`}
                </p>
              </CardContent>
            </Card>

            {/* Market Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Market Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">
                      ${avgPrice > 1000000 ? (avgPrice / 1000000).toFixed(1) + 'M' : (avgPrice / 1000).toFixed(0) + 'K'}
                    </div>
                    <div className="text-gray-500 text-sm">Average Price</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">{avgDaysOnMarket}</div>
                    <div className="text-gray-500 text-sm">Days on Market</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">{activeListings}</div>
                    <div className="text-gray-500 text-sm">{liveData?.dataType === 'sold' ? 'Recently Sold' : 'Active Listings'}</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${priceChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {priceChange >= 0 ? '+' : ''}{priceChange}%
                    </div>
                    <div className="text-gray-500 text-sm">Price Change (YoY)</div>
                  </div>
                </div>

                {/* Price Range Info */}
                {liveData?.priceRange && (
                  <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Price Range in {displayName}</h4>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Starting from</span>
                      <span className="font-bold text-green-600">${(liveData.priceRange.min / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600">Up to</span>
                      <span className="font-bold text-red-600">${(liveData.priceRange.max / 1000000).toFixed(2)}M</span>
                    </div>
                    {liveData.medianPrice > 0 && (
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-600">Median Price</span>
                        <span className="font-bold text-gray-900">${(liveData.medianPrice / 1000).toFixed(0)}K</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Price History Chart - if mock data has it */}
                {mockData?.priceHistory && (
                  <div className="mt-8">
                    <h4 className="font-semibold text-gray-900 mb-4">6-Month Price Trend</h4>
                    <div className="h-40 flex items-end gap-2">
                      {mockData.priceHistory.map((item, idx) => {
                        const maxPrice = Math.max(...mockData.priceHistory.map(p => p.price));
                        const minPrice = Math.min(...mockData.priceHistory.map(p => p.price));
                        const range = maxPrice - minPrice || 1;
                        const height = ((item.price - minPrice) / range) * 100 + 20;
                        
                        return (
                          <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                            <div 
                              className="w-full bg-red-600 rounded-t transition-all hover:bg-red-700"
                              style={{ height: `${height}%` }}
                              title={`$${item.price.toLocaleString()}`}
                            />
                            <span className="text-xs text-gray-500">{item.month}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Neighbourhood Ratings - only show if mock data has it */}
            {mockData && (
              <Card>
                <CardHeader>
                  <CardTitle>Neighbourhood Ratings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {mockData.walkScore && (
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="flex items-center gap-2 text-gray-700">
                          <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <MapPin size={16} className="text-blue-600" />
                          </span>
                          Walk Score
                        </span>
                        <span className="font-bold text-gray-900">{mockData.walkScore}/100</span>
                      </div>
                      <Progress value={mockData.walkScore} className="h-2" />
                    </div>
                  )}

                  {mockData.transitScore && (
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="flex items-center gap-2 text-gray-700">
                          <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <Home size={16} className="text-green-600" />
                          </span>
                          Transit Score
                        </span>
                        <span className="font-bold text-gray-900">{mockData.transitScore}/100</span>
                      </div>
                      <Progress value={mockData.transitScore} className="h-2" />
                    </div>
                  )}

                  {mockData.schoolRating && (
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="flex items-center gap-2 text-gray-700">
                          <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <GraduationCap size={16} className="text-purple-600" />
                          </span>
                          School Rating
                        </span>
                        <span className="font-bold text-gray-900">{mockData.schoolRating}/10</span>
                      </div>
                      <Progress value={mockData.schoolRating * 10} className="h-2" />
                    </div>
                  )}

                  {mockData.amenitiesRating && (
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="flex items-center gap-2 text-gray-700">
                          <span className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                            <ShoppingBag size={16} className="text-amber-600" />
                          </span>
                          Amenities
                        </span>
                        <span className="font-bold text-gray-900">{mockData.amenitiesRating}/10</span>
                      </div>
                      <Progress value={mockData.amenitiesRating * 10} className="h-2" />
                    </div>
                  )}

                  {mockData.crimeRate && (
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="flex items-center gap-2 text-gray-700">
                          <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                            <Shield size={16} className="text-red-600" />
                          </span>
                          Safety
                        </span>
                        <Badge className={`${mockData.crimeRate === 'Very Low' ? 'bg-green-600' : mockData.crimeRate === 'Low' ? 'bg-green-500' : 'bg-amber-500'}`}>
                          {mockData.crimeRate} Crime Rate
                        </Badge>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Listings in Area */}
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Homes for Sale in {displayName}</h2>
                <Link to={`/search?location=${id}`} className="text-red-600 font-medium flex items-center gap-1 hover:text-red-700">
                  View All <ArrowRight size={16} />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {displayListings.map((listing) => (
                  <Link key={listing.id} to={`/property/${listing.id}`}>
                    <Card className="group overflow-hidden hover:shadow-lg transition-all">
                      <div className="relative h-40 overflow-hidden">
                        <img 
                          src={listing.image} 
                          alt={listing.address}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute bottom-2 left-2">
                          <Badge className="bg-black/70">${listing.price.toLocaleString()}</Badge>
                        </div>
                      </div>
                      <CardContent className="p-3">
                        <h4 className="font-medium text-gray-900 truncate">{listing.address}</h4>
                        <p className="text-gray-500 text-sm">{listing.beds} bed • {listing.baths} bath {listing.sqft ? `• ${listing.sqft} sqft` : ''}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <LeadCaptureForm 
              variant="compact"
              title={`Explore ${displayName}`}
              subtitle="Get personalized property recommendations"
            />

            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-4">Quick Stats</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Average Price</span>
                    <span className="font-medium">${avgPrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{liveData?.dataType === 'sold' ? 'Recently Sold' : 'Active Listings'}</span>
                    <span className="font-medium">{activeListings}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Avg Days on Market</span>
                    <span className="font-medium">{avgDaysOnMarket}</span>
                  </div>
                  {mockData?.walkScore && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Walk Score</span>
                      <span className="font-medium">{mockData.walkScore}</span>
                    </div>
                  )}
                  {mockData?.transitScore && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Transit Score</span>
                      <span className="font-medium">{mockData.transitScore}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NeighbourhoodDetailPage;
